/**
 * How many words a mention search may span, the `@` word included.
 *
 * Three, because real page names need it: `Coca Cola`, `Red Bull`,
 * `The Coca-Cola Company`. Meta's `/pages/search` handles multi-word queries
 * well — a live `"Coca cola"` returns 25 Coca-Cola pages where `"Coca"` alone
 * returns mostly `Cocoa` noise — so the only thing that ever stopped users
 * searching two words was the editor, not the search.
 */
export const MENTION_QUERY_MAX_WORDS = 3;

/**
 * Whether a mention typeahead query is still plausibly a page name rather than
 * ordinary prose that happens to follow an `@`.
 *
 * The tiptap suggestion plugin is run with `allowSpaces: true`, whose matcher
 * runs from the `@` to the cursor with no word limit of its own. Without this
 * bound the popup would follow the user for the rest of the line, firing a
 * search on every sentence containing an `@`. With it, the popup closes once
 * the span stops looking like a name and reopens if the user deletes back.
 *
 * A leading space is rejected outright: `"email me @ the office"` uses `@` as a
 * word, and no page search should ever run for it.
 */
export function isMentionQueryWithinBound(
  query: string,
  maxWords: number = MENTION_QUERY_MAX_WORDS,
): boolean {
  if (query.startsWith(' ')) {
    return false;
  }

  // `filter(Boolean)` so a trailing space counts as no extra word — the popup
  // has to survive the keystroke between "Coca" and "Cola".
  return query.split(/\s+/).filter(Boolean).length <= maxWords;
}
